import React from 'react'
import DashboardSectionCard from '@components/pages/Dashboard/DashboardSectionCard.component'
import DashboardStackedBarChart from '@components/pages/Dashboard/MetricCharts/DashboardStackedBarChart.component'
import DashboardSectionEmptyState from '@components/pages/Dashboard/DashboardSectionEmptyState.component'
import type { UseTreatmentValueResult } from '@hooks/dashboard/useTreatmentValue'
import {
  formatChartCurrencyKUnits,
  HYGIENE_PLACEHOLDER,
} from '@utils/dashboardFinance.util'
import {
  isDashboardSectionPending,
  shouldShowDashboardSectionEmpty,
} from '@utils/dashboardSectionEmpty.util'
import {
  FINANCE_TREATMENT_VALUE_BAR_SIZE,
  TREATMENT_VALUE_COLORS,
} from '@constants/Dashboard/financeDashboard.constants'
import {
  FinanceMetricTilesRow,
  FinanceTreatmentValueBarLegend,
} from '../../FinanceMetricTiles.component'

/** Figma 5462:119617 — 150px bars with gap between Billed / Collected / Potential. */
const TREATMENT_VALUE_BAR_CHART_PROPS = {
  barSize: FINANCE_TREATMENT_VALUE_BAR_SIZE,
  barCategoryGap: '18%',
} as const

type TreatmentValueCardProps = {
  titleHref: string
  treatmentValue: UseTreatmentValueResult
}

export function TreatmentValueCard({
  titleHref,
  treatmentValue,
}: TreatmentValueCardProps): React.ReactElement {
  const {
    loading,
    billedStr,
    collectedStr,
    potentialStr,
    chart,
    hasData,
    hasPayload,
  } = treatmentValue
  const isEmpty = shouldShowDashboardSectionEmpty(loading, hasData)
  const pending = isDashboardSectionPending(loading, hasPayload)

  return (
    <DashboardSectionCard
      title="Treatment value"
      titleHref={titleHref}
      className="dashboard-finance-treatment-value-card"
      loading={pending}
      skeleton={{ metrics: 3, metricColumns: 3, block: 'chart' }}
      isEmpty={isEmpty}
      disableTitleLink={isEmpty}
    >
      <div className="dashboard-finance-treatment-value">
        <FinanceMetricTilesRow
          columns={3}
          metrics={[
            {
              id: 'billed',
              leftLabel: 'Billed',
              value: loading ? HYGIENE_PLACEHOLDER : billedStr,
            },
            {
              id: 'collected',
              leftLabel: 'Collected',
              value: loading ? HYGIENE_PLACEHOLDER : collectedStr,
            },
            {
              id: 'potential',
              leftLabel: 'Potential',
              value: loading ? HYGIENE_PLACEHOLDER : potentialStr,
            },
          ]}
        />
        {isEmpty ? (
          <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
        ) : (
          <div className="dashboard-finance-treatment-value-chart-wrap dashboard-finance-bar-chart-legend-spaced">
            <DashboardStackedBarChart
              data={loading ? [] : chart.data}
              series={[{ dataKey: 'value', name: '', color: '' }]}
              horizontal={false}
              stacked={false}
              categoryIndicatorColors={TREATMENT_VALUE_COLORS}
              hideCategoryIndicatorCircles
              hideCategoryAxisLabels
              valueFormatter={formatChartCurrencyKUnits}
              height={220}
              yAxisDomain={chart.yDomain}
              yAxisTicks={chart.yTicks}
              showLegend={false}
              className="dashboard-finance-treatment-value-chart"
              {...TREATMENT_VALUE_BAR_CHART_PROPS}
            />
            <FinanceTreatmentValueBarLegend />
          </div>
        )}
      </div>
    </DashboardSectionCard>
  )
}
